import { useEffect, useState } from "react";
import { api } from "@/api/client";
import type { components } from "@dolfin/api-admin";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { CopyButton } from "@/components/ui/copy-button";
import { Spinner } from "@/components/ui/spinner";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { PaginationBar } from "@/components/PaginationBar";
import { useCursorPagination } from "@/hooks/useCursorPagination";
import { formatDateTime, formatNumber } from "@/lib/format";
import { CheckCircle2Icon, XCircleIcon, ChevronRightIcon } from "lucide-react";

type McpSessionSummary = components["schemas"]["McpSessionSummaryResponse"];
type McpSessionDetail = components["schemas"]["McpSessionDetailResponse"];
type McpInvocation = components["schemas"]["McpToolInvocationResponse"];

interface McpSessionsTableProps {
  organisationId: string;
}

interface DetailState {
  loading: boolean;
  error: string | null;
  detail: McpSessionDetail | null;
  invocations: McpInvocation[];
  invocationsCursor?: string;
  invocationsHasMore?: boolean;
  invocationsLoading: boolean;
}

const INVOCATION_PAGE_SIZE = 20;

export function McpSessionsTable({ organisationId }: McpSessionsTableProps) {
  const pagination = useCursorPagination({ initialLimit: 20 });

  const [sessions, setSessions] = useState<McpSessionSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [openSessionIds, setOpenSessionIds] = useState<string[]>([]);
  const [details, setDetails] = useState<Record<string, DetailState>>({});

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    api
      .GET("/v1/mcp/{organisationId}/sessions", {
        params: {
          path: { organisationId },
          query: {
            Cursor: pagination.cursor,
            Limit: pagination.limit,
          },
        },
      })
      .then(({ data, error: apiError }) => {
        if (cancelled) return;
        if (apiError) {
          setError("Failed to load MCP sessions.");
        } else {
          setSessions(data?.data ?? []);
          pagination.setPage({
            nextCursor: data?.meta?.cursors?.next,
            previousCursor: data?.meta?.cursors?.previous,
          });
        }
        setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [organisationId, pagination.cursor, pagination.limit]);

  async function loadDetail(sessionId: string) {
    setDetails((prev) => ({
      ...prev,
      [sessionId]: {
        loading: true,
        error: null,
        detail: null,
        invocations: [],
        invocationsLoading: false,
      },
    }));

    const { data, error: apiError } = await api.GET(
      "/v1/mcp/{organisationId}/sessions/{id}",
      {
        params: { path: { organisationId, id: sessionId } },
      },
    );

    if (apiError || !data) {
      setDetails((prev) => ({
        ...prev,
        [sessionId]: {
          loading: false,
          error: "Failed to load session detail.",
          detail: null,
          invocations: [],
          invocationsLoading: false,
        },
      }));
      return;
    }

    setDetails((prev) => ({
      ...prev,
      [sessionId]: {
        loading: false,
        error: null,
        detail: data,
        invocations: data.invocations ?? [],
        invocationsLoading: false,
      },
    }));
  }

  async function loadMoreInvocations(sessionId: string) {
    const current = details[sessionId];
    if (!current || current.invocationsLoading) return;

    setDetails((prev) => ({
      ...prev,
      [sessionId]: { ...current, invocationsLoading: true },
    }));

    const { data, error: apiError } = await api.GET(
      "/v1/mcp/{organisationId}/sessions/{sessionId}/invocations",
      {
        params: {
          path: { organisationId, sessionId },
          query: {
            Cursor: current.invocationsCursor,
            Limit: INVOCATION_PAGE_SIZE,
          },
        },
      },
    );

    if (apiError || !data) {
      setDetails((prev) => ({
        ...prev,
        [sessionId]: {
          ...current,
          invocationsLoading: false,
          error: "Failed to load more invocations.",
        },
      }));
      return;
    }

    setDetails((prev) => ({
      ...prev,
      [sessionId]: {
        ...current,
        invocations: [...current.invocations, ...(data.data ?? [])],
        invocationsCursor: data.meta?.cursors?.next ?? undefined,
        invocationsHasMore: data.meta?.cursors?.next != null,
        invocationsLoading: false,
      },
    }));
  }

  function handleAccordionChange(values: string[]) {
    setOpenSessionIds(values);
    for (const id of values) {
      if (!details[id]) {
        loadDetail(id);
      }
    }
  }

  if (error) {
    return <p className="text-destructive">{error}</p>;
  }

  return (
    <div>
      <div className="mb-4 flex items-center justify-between">
        <p className="text-sm text-muted-foreground">
          Each session is one connection from an MCP host (Claude Desktop, Cursor, claude.ai, …).
          Expand a session to see its tool invocations.
        </p>
        {loading && <Spinner className="size-5" />}
      </div>

      <div className="rounded-md border">
        {sessions.length === 0 && !loading ? (
          <div className="text-muted-foreground p-8 text-center text-sm">
            No MCP sessions found.
          </div>
        ) : (
          <Accordion
            type="multiple"
            value={openSessionIds}
            onValueChange={handleAccordionChange}
          >
            {sessions.map((session) => (
              <AccordionItem
                key={session.id}
                value={session.id ?? ""}
                className="px-4"
              >
                <AccordionTrigger className="hover:no-underline">
                  <div className="grid w-full grid-cols-12 items-center gap-3 pr-2 text-sm">
                    <div className="col-span-3 flex items-center gap-2">
                      <span className="font-mono text-xs text-muted-foreground">
                        {session.mcpSessionId
                          ? session.mcpSessionId.slice(0, 12) + "…"
                          : "—"}
                      </span>
                    </div>
                    <div className="col-span-2">
                      {session.apiKeyId ? (
                        <Badge variant="secondary" className="font-mono text-xs">
                          {session.apiKeyId.slice(0, 8)}…
                        </Badge>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </div>
                    <div className="col-span-2 text-right tabular-nums">
                      {formatNumber(session.callCount)} calls
                    </div>
                    <div className="col-span-2 text-muted-foreground">
                      Last: {formatDateTime(session.lastCallAt)}
                    </div>
                    <div className="col-span-3 text-muted-foreground">
                      Started: {formatDateTime(session.startedAt)}
                    </div>
                  </div>
                </AccordionTrigger>
                <AccordionContent>
                  {session.id && (
                    <SessionDetailPanel
                      sessionId={session.id}
                      summary={session}
                      state={details[session.id]}
                      onLoadMore={() => loadMoreInvocations(session.id!)}
                    />
                  )}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        )}
      </div>

      <PaginationBar
        hasPrevious={pagination.hasPrevious}
        hasNext={pagination.hasMore}
        isLoading={loading}
        limit={pagination.limit}
        onPrevious={pagination.goPrevious}
        onNext={pagination.goNext}
        onLimitChange={pagination.setLimit}
      />
    </div>
  );
}

interface SessionDetailPanelProps {
  sessionId: string;
  summary: McpSessionSummary;
  state: DetailState | undefined;
  onLoadMore: () => void;
}

function SessionDetailPanel({ summary, state, onLoadMore }: SessionDetailPanelProps) {
  if (!state || state.loading) {
    return (
      <div className="flex justify-center py-6">
        <Spinner className="size-5" />
      </div>
    );
  }

  if (state.error) {
    return <p className="py-4 text-sm text-destructive">{state.error}</p>;
  }

  const detail = state.detail;
  if (!detail) return null;

  const successCount = state.invocations.filter((i) => i.success).length;
  const failureCount = state.invocations.filter((i) => i.success === false).length;
  const avgDuration =
    state.invocations.length > 0
      ? Math.round(
          state.invocations.reduce(
            (sum, i) => sum + (Number(i.durationMs) || 0),
            0,
          ) / state.invocations.length,
        )
      : 0;

  return (
    <div className="space-y-4 pb-2">
      <div className="rounded-md border bg-muted/30 p-3">
        <h4 className="mb-2 text-xs font-bold tracking-wider text-muted-foreground uppercase">
          Session
        </h4>
        <dl className="grid [&>div]:min-w-0 grid-cols-1 gap-x-6 gap-y-2 text-sm sm:grid-cols-3">
          <DetailField label="Session ID">
            {detail.id ? (
              <span className="flex items-center gap-1">
                <Badge variant="secondary" className="font-mono text-xs">
                  {detail.id}
                </Badge>
                <CopyButton value={detail.id} />
              </span>
            ) : (
              "—"
            )}
          </DetailField>
          <DetailField label="MCP Session ID">
            {detail.mcpSessionId ? (
              <span className="flex items-center gap-1">
                <Badge variant="secondary" className="font-mono text-xs break-all">
                  {detail.mcpSessionId}
                </Badge>
                <CopyButton value={detail.mcpSessionId} />
              </span>
            ) : (
              "—"
            )}
          </DetailField>
          <DetailField label="API Key ID">
            {detail.apiKeyId ? (
              <span className="flex items-center gap-1">
                <Badge variant="secondary" className="font-mono text-xs">
                  {detail.apiKeyId}
                </Badge>
                <CopyButton value={detail.apiKeyId} />
              </span>
            ) : (
              <span className="text-muted-foreground">—</span>
            )}
          </DetailField>
          <DetailField label="Started">{formatDateTime(detail.startedAt)}</DetailField>
          <DetailField label="Last Call">{formatDateTime(detail.lastCallAt)}</DetailField>
          <DetailField label="Total Calls">
            {formatNumber(detail.callCount ?? summary.callCount)}
          </DetailField>
          <DetailField label="Loaded">
            {formatNumber(state.invocations.length)} invocations
          </DetailField>
          <DetailField label="Success / Failure">
            <span className="inline-flex items-center gap-2">
              <span className="inline-flex items-center gap-1 text-green-600">
                <CheckCircle2Icon className="size-3.5" />
                {formatNumber(successCount)}
              </span>
              <span className="inline-flex items-center gap-1 text-destructive">
                <XCircleIcon className="size-3.5" />
                {formatNumber(failureCount)}
              </span>
            </span>
          </DetailField>
          {avgDuration > 0 && (
            <DetailField label="Avg Duration">{formatNumber(avgDuration)} ms</DetailField>
          )}
        </dl>
      </div>

      <div>
        <h4 className="mb-2 text-xs font-bold tracking-wider text-muted-foreground uppercase">
          Invocations
        </h4>
        {state.invocations.length === 0 ? (
          <p className="text-muted-foreground text-sm">No invocations recorded.</p>
        ) : (
          <div className="space-y-1">
            {state.invocations.map((invocation, idx) => (
              <InvocationRow
                key={invocation.id ?? idx}
                invocation={invocation}
                index={idx}
              />
            ))}
          </div>
        )}
        {state.invocationsHasMore && (
          <div className="mt-3 flex justify-center">
            <Button
              variant="outline"
              size="sm"
              onClick={onLoadMore}
              disabled={state.invocationsLoading}
            >
              {state.invocationsLoading ? (
                <Spinner className="size-4" />
              ) : (
                "Load more invocations"
              )}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}

function DetailField({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="min-w-0">
      <dt className="text-xs text-muted-foreground">{label}</dt>
      <dd className="mt-0.5 break-words">{children}</dd>
    </div>
  );
}

interface InvocationRowProps {
  invocation: McpInvocation;
  index: number;
}

function InvocationRow({ invocation, index }: InvocationRowProps) {
  const [open, setOpen] = useState(false);

  const success = invocation.success;
  const hasArgs = !!invocation.argumentsJson;
  const hasResult = !!invocation.resultJson;

  return (
    <div className="rounded-md border">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center gap-3 px-3 py-2 text-left text-sm hover:bg-accent/50"
      >
        <ChevronRightIcon
          className={`size-4 shrink-0 text-muted-foreground transition-transform ${open ? "rotate-90" : ""}`}
        />
        <span className="text-muted-foreground tabular-nums w-8 shrink-0">
          #{index + 1}
        </span>
        {success === true ? (
          <CheckCircle2Icon className="size-4 shrink-0 text-green-600" />
        ) : success === false ? (
          <XCircleIcon className="size-4 shrink-0 text-destructive" />
        ) : (
          <span className="size-4 shrink-0" />
        )}
        <span className="flex-1 truncate font-mono text-xs">{invocation.toolName}</span>
        <span className="shrink-0 tabular-nums text-xs text-muted-foreground">
          {formatNumber(invocation.durationMs)} ms
        </span>
        <span className="shrink-0 text-xs text-muted-foreground">
          {formatDateTime(invocation.createdAt)}
        </span>
      </button>
      {open && (
        <div className="space-y-3 border-t bg-muted/20 p-3">
          <dl className="grid [&>div]:min-w-0 grid-cols-1 gap-x-6 gap-y-2 text-xs sm:grid-cols-3">
            <DetailField label="Invocation ID">
              {invocation.id ? (
                <span className="flex items-center gap-1">
                  <Badge variant="secondary" className="font-mono text-xs">
                    {invocation.id}
                  </Badge>
                  <CopyButton value={invocation.id} />
                </span>
              ) : (
                "—"
              )}
            </DetailField>
            <DetailField label="Tool">
              <span className="font-mono">{invocation.toolName}</span>
            </DetailField>
            <DetailField label="Trace ID">
              {invocation.traceId ? (
                <span className="flex items-center gap-1">
                  <Badge variant="secondary" className="font-mono text-xs">
                    {invocation.traceId}
                  </Badge>
                  <CopyButton value={invocation.traceId} />
                </span>
              ) : (
                <span className="text-muted-foreground">—</span>
              )}
            </DetailField>
          </dl>

          {hasArgs && (
            <JsonBlock label="Arguments" json={invocation.argumentsJson!} />
          )}
          {hasResult && <JsonBlock label="Result" json={invocation.resultJson!} />}
          {!hasArgs && !hasResult && (
            <p className="text-muted-foreground text-xs">No arguments or result recorded.</p>
          )}
        </div>
      )}
    </div>
  );
}

function JsonBlock({ label, json }: { label: string; json: string }) {
  let pretty = json;
  try {
    pretty = JSON.stringify(JSON.parse(json), null, 2);
  } catch {
    // leave as-is if not valid JSON
  }

  return (
    <div>
      <div className="mb-1 flex items-center justify-between">
        <span className="text-xs font-medium text-muted-foreground">{label}</span>
        <CopyButton value={pretty} />
      </div>
      <pre className="max-h-96 overflow-auto rounded border bg-background p-2 font-mono text-xs whitespace-pre-wrap break-all">
        {pretty}
      </pre>
    </div>
  );
}
